// components
import {
  ControlledAutoComplete,
  ControlledDatePicker,
  ControlledTextField
} from 'components/shared/ControlledFields';
import {
  StyledFilterComponentBox,
  StyledFilterFieldBox
} from './FilterComponent.styles';
import { StyledFlexGrowBox } from 'components/shared/Box/Box.styles';

// consts
import { defaultValues } from './FilterComponent.consts';

// hooks
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetPoolMaintenanceTableData } from 'components/admin-portal/Tables/Pools/PoolMaintenance/hooks/useGetPoolMaintenanceTableData';
import { useGetClusters, useGetEmployers } from 'queries';
import { isNil } from 'ramda';
import { useSelector } from 'hooks';
import { useDebounceValue } from 'usehooks-ts';

// types
import {
  FilterComponentProps,
  FilterForm,
  Filters,
  NoEmployeePoolOption,
  NoEmployerOption,
  NoPoolOption
} from './FilterComponent.types';

// utils
import {
  isNoEmployeeOption,
  isNoEmployerOption,
  isNoPoolOption
} from './FilterComponent.utils';

export const FilterComponent = ({
  handleFilterChange,
  rows,
  dependencies = []
}: FilterComponentProps) => {
  const { t } = useTranslation('common');
  const { control, watch } = useForm<FilterForm>({
    defaultValues: defaultValues
  });
  const { hasClustersOrgGrant, hasPoolsOrgGrant } = useSelector(
    (state) => state.grants
  );
  const { data: pools } = useGetPoolMaintenanceTableData({
    enabled: hasPoolsOrgGrant
  });
  const { data: clusters } = useGetClusters({ enabled: hasClustersOrgGrant });
  const { data: employers } = useGetEmployers();
  const [filters, setFilters] = useState<Filters>(defaultValues);
  let clearedPlanUnitsDetails: { brId: number; name?: string }[] = [];

  const referenceDate = watch('referenceDate');
  const search = watch('search');
  const cluster = watch('cluster');
  const requestPool = watch('requestPool');
  const employeePool = watch('employeePool');
  const employer = watch('employer');
  const [debouncedValue, setDebouncedValue] = useDebounceValue(search, 300);

  const updateFilter = (value, propName) => {
    setFilters((prev) => ({ ...prev, [propName]: value }));
  };

  useEffect(() => {
    setDebouncedValue(search);
  }, [search, setDebouncedValue]);

  useEffect(() => {
    const updateFilters = {
      referenceDate: dayjs(referenceDate).isValid() ? referenceDate : null,
      search: debouncedValue ?? null,
      clusterId: cluster?.clusterId ?? null,
      poolId: isNoPoolOption(requestPool) ? null : requestPool?.id ?? null,
      noPool: isNoPoolOption(requestPool),
      noEmployeePool: isNoEmployeeOption(employeePool),
      empPoolId: employeePool?.id ?? null,
      employerId: isNoEmployerOption(employer)
        ? null
        : employer?.employerId ?? null,
      noEmployer: isNoEmployerOption(employer)
    };

    Object.entries(updateFilters).forEach(([key, value]) =>
      updateFilter(value, key)
    );
  }, [
    debouncedValue,
    cluster,
    requestPool,
    employeePool,
    employer,
    referenceDate,
    ...dependencies
  ]);

  useEffect(() => {
    handleFilterChange(filters);
  }, [filters, ...dependencies]);

  if (!isNil(rows) && !isNil(referenceDate)) {
    rows.forEach((item) => {
      if (!isNil(item.brId)) {
        if (item.valid && new Date(item.valid.to) >= referenceDate) {
          clearedPlanUnitsDetails.push({
            brId: item.brId,
            name: item.name
          });
        }
      }
    });
    clearedPlanUnitsDetails = clearedPlanUnitsDetails
      .filter(({ name }) => name) // filter out nameless plan units
      .sort((a, b) => {
        return (a?.name ?? '').localeCompare(b?.name ?? '');
      });
  }

  const noEmployerOption: NoEmployerOption = {
    noEmployer: true,
    name: t('noEmployer')
  };
  const noPoolOption: NoPoolOption = { noPool: true, name: t('noPool') };
  const noEmployeeOption: NoEmployeePoolOption = {
    noEmployeePool: true,
    name: t('noEmployeePool')
  };

  return (
    <StyledFilterComponentBox>
      <ControlledDatePicker
        variant="outlined"
        name="referenceDate"
        label={t('referenceDate')}
        control={control}
        showTodayButton
      />
      <StyledFlexGrowBox>
        <ControlledTextField
          control={control}
          name="search"
          label={t('search')}
        />
      </StyledFlexGrowBox>
      {hasClustersOrgGrant && (
        <StyledFilterFieldBox>
          <ControlledAutoComplete
            disableSorting
            optionLabelProp="name"
            idProperty="clusterId"
            variant="outlined"
            control={control}
            name="cluster"
            label={t('cluster')}
            options={clusters ?? []}
            fullWidth
          />
        </StyledFilterFieldBox>
      )}
      <StyledFilterFieldBox>
        <ControlledAutoComplete
          disableSorting
          optionLabelProp="name"
          idProperty="employerId"
          variant="outlined"
          control={control}
          name="employer"
          label={capitalize(t('employer'))}
          options={[noEmployerOption, ...(employers ?? [])]}
          fullWidth
        />
      </StyledFilterFieldBox>
      {hasPoolsOrgGrant && (
        <>
          <StyledFilterFieldBox>
            <ControlledAutoComplete
              disableSorting
              optionLabelProp="name"
              variant="outlined"
              control={control}
              name="requestPool"
              label={t('requestPool')}
              options={[noPoolOption, ...(pools ?? [])]}
              fullWidth
            />
          </StyledFilterFieldBox>
          <StyledFilterFieldBox>
            <ControlledAutoComplete
              disableSorting
              optionLabelProp="name"
              variant="outlined"
              control={control}
              name="employeePool"
              label={t('employeePool')}
              options={[noEmployeeOption, ...(pools ?? [])]}
              fullWidth
            />
          </StyledFilterFieldBox>
        </>
      )}
    </StyledFilterComponentBox>
  );
};
